'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Reading-progress hairline for long editorial pages (/expanded-access,
 * /right-to-try): a 2px ink rule that grows across the viewport, docked to the
 * bottom edge of the fixed marketing header. Progress feedback stays useful
 * under prefers-reduced-motion, so only the easing transition is disabled.
 */
export default function MarketingReadingProgress() {
  const barRef = useRef<HTMLDivElement | null>(null);
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    const updateTop = () => {
      const header = document.querySelector<HTMLElement>('.marketing-navbar');
      setTop(header ? Math.round(header.getBoundingClientRect().bottom) : 0);
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    // The header slides 56px when the promo banner is dismissed (body class flips)
    const observer = new MutationObserver(updateTop);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!barRef.current) return;
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        barRef.current.style.transform = `scaleX(${progress})`;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', onScroll);
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  if (top === null) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed left-0 right-0 z-40 h-0.5"
      style={{ top }}
    >
      <div
        ref={barRef}
        className="h-full w-full origin-left bg-primary transition-transform duration-150 ease-out motion-reduce:transition-none"
        style={{ transform: 'scaleX(0)' }}
      />
    </div>
  );
}
